import React from "react";
import { useTranslation } from "react-i18next";

import {
  useRetrieveOptimizedGantt,
  useRetrieveWbsMeta,
} from "@planzone/view-models";
import { events } from "@planzone/events";
import { GanttFilter } from "@planzone/dto";
import { useMessage } from "@components/useMessage";
import { usePreferences } from "@components/preferences";
import { Wbs } from "@scenes/wbs";
import { createHoverStore, HoverProvider } from "@common/elementHover";
import { InfiniteScroll } from "@common/infiniteScroll";
import background from "./background.svg";
import { Props } from "./types";
import styles from "./styles.scss";

function Tutorial({ project }: Props) {
  const { t } = useTranslation();
  const [hoverStore] = React.useState(createHoverStore);
  const { state, edit } = usePreferences();
  const { wbsFilter, wbsSort: sort } = state;
  const filter: GanttFilter = {
    ...wbsFilter,
    scope: "project",
    projects: [project],
  };
  const { gantt } = useRetrieveOptimizedGantt(
    filter,
    sort === "zone" ? "ascendingRank" : sort,
    10,
  );
  const {
    isRequestPending: isMetaRequestPending,
    isRequestSuccess: isMetaRequestSuccess,
    isRequestFailure: isMetaRequestFailure,
    meta,
    retrieveWbsMeta,
  } = useRetrieveWbsMeta([project]);

  React.useEffect(() => {
    !isMetaRequestPending &&
      !isMetaRequestSuccess &&
      !isMetaRequestFailure &&
      retrieveWbsMeta({ projects: [project] });
  }, [project.uuid]);

  useMessage(
    ({ type }) =>
      (type === events.moveListElementSucceeded ||
        ["Task", "List", "Milestone", "Event"].find(
          (elementType) =>
            type === `create${elementType}Succeeded` ||
            type === `remove${elementType}Succeeded`,
        )) &&
      retrieveWbsMeta({ projects: [project] }),
  );

  return (
    <InfiniteScroll id="timeSheet" className={styles.tutorial} canFetchOnIdle>
      {() => (
        <>
          <img src={background} alt="" className={styles.background} />
          <div className={styles.main}>
            <h2 className={styles.title}>{t("timeSheet.tutorial.title")}</h2>
            <p className={styles.message}>
              <span className={styles.action}>
                {t("timeSheet.tutorial.action")}
              </span>{" "}
              {t("timeSheet.tutorial.message")}
            </p>
            <div className={styles.wbs}>
              <HoverProvider value={hoverStore}>
                <Wbs
                  projects={[project]}
                  gantt={gantt}
                  filter={filter}
                  sort={sort === "zone" ? "ascendingRank" : sort}
                  meta={meta}
                  loaderId="timeSheet"
                  configuration={[{ key: "allocations" }]}
                  hideProjects
                />
              </HoverProvider>
            </div>
          </div>
        </>
      )}
    </InfiniteScroll>
  );
}

export { Tutorial };
